// Cloudflare API Service Layer
// Handles all API calls to Cloudflare Workers + D1

class CloudflareAPI {
  constructor() {
    // Auto-detect API URL based on environment
    this.baseURL = process.env.NODE_ENV === 'production'
      ? '/api'  // Production: Cloudflare Pages Functions
      : 'http://localhost:8788/api';  // Local: Wrangler dev server
  }

  // ===========================
  // HERO SLIDES API
  // ===========================

  async getHeroSlides() {
    try {
      const response = await fetch(`${this.baseURL}/hero-slides`);
      if (!response.ok) throw new Error('Failed to fetch hero slides');
      return await response.json();
    } catch (error) {
      console.error('Error fetching hero slides:', error);
      throw error;
    }
  }

  async createHeroSlide(data) {
    try {
      const response = await fetch(`${this.baseURL}/hero-slides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create hero slide');
      return await response.json();
    } catch (error) {
      console.error('Error creating hero slide:', error);
      throw error;
    }
  }

  async updateHeroSlide(id, data) {
    try {
      console.log('🔄 Sending PUT request:', { id, ...data });
      const response = await fetch(`${this.baseURL}/hero-slides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });

      if (!response.ok) {
        // Capture the actual error response body
        const errorText = await response.text();
        console.error('❌ PUT request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update hero slide: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT request successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating hero slide:', error);
      throw error;
    }
  }

  async deleteHeroSlide(id) {
    try {
      const response = await fetch(`${this.baseURL}/hero-slides?id=${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete hero slide');
      return await response.json();
    } catch (error) {
      console.error('Error deleting hero slide:', error);
      throw error;
    }
  }

  // ===========================
  // TESTIMONIALS API (Coming soon)
  // ===========================

  // Placeholder for testimonials API
  // Will implement after hero slides are working

  // ===========================
  // HOME SERVICE LISTS API (for home page only)
  // ===========================

  async getHomeServiceLists() {
    try {
      const response = await fetch(`${this.baseURL}/home-service-lists`);
      if (!response.ok) throw new Error('Failed to fetch home service lists');
      return await response.json();
    } catch (error) {
      console.error('Error fetching home service lists:', error);
      throw error;
    }
  }

  async createHomeServiceList(data) {
    try {
      console.log('🔄 Sending POST request to create home service list:', data);
      const response = await fetch(`${this.baseURL}/home-service-lists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST home service list failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create home service list: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ POST home service list successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating home service list:', error);
      throw error;
    }
  }

  async updateHomeServiceList(id, data) {
    try {
      console.log('🔄 Sending PUT request to update home service list:', { id, ...data });
      const response = await fetch(`${this.baseURL}/home-service-lists`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT home service list failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update home service list: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT home service list successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating home service list:', error);
      throw error;
    }
  }

  async deleteHomeServiceList(id) {
    try {
      const response = await fetch(`${this.baseURL}/home-service-lists?id=${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete home service list');
      return await response.json();
    } catch (error) {
      console.error('Error deleting home service list:', error);
      throw error;
    }
  }

  // ===========================
  // HOME WHYCHOOSE FEATURES API (for home page "Why Choose Us" section)
  // ===========================

  async getHomeWhyChooseFeatures() {
    try {
      const response = await fetch(`${this.baseURL}/home-whychoose-features`);
      if (!response.ok) throw new Error('Failed to fetch home whychoose features');
      return await response.json();
    } catch (error) {
      console.error('Error fetching home whychoose features:', error);
      throw error;
    }
  }

  async createHomeWhyChooseFeature(data) {
    try {
      console.log('🔄 Sending POST request to create home whychoose feature:', data);
      const response = await fetch(`${this.baseURL}/home-whychoose-features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST home whychoose feature failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create home whychoose feature: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ POST home whychoose feature successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating home whychoose feature:', error);
      throw error;
    }
  }

  async updateHomeWhyChooseFeature(id, data) {
    try {
      console.log('🔄 Sending PUT request to update home whychoose feature:', { id, ...data });
      const response = await fetch(`${this.baseURL}/home-whychoose-features`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT home whychoose feature failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update home whychoose feature: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT home whychoose feature successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating home whychoose feature:', error);
      throw error;
    }
  }

  async deleteHomeWhyChooseFeature(id) {
    try {
      const response = await fetch(`${this.baseURL}/home-whychoose-features?id=${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete home whychoose feature');
      return await response.json();
    } catch (error) {
      console.error('Error deleting home whychoose feature:', error);
      throw error;
    }
  }

  // ===========================
  // HOME PROGRESS STATS API (for progress/stats section - single row)
  // ===========================

  async getHomeProgressStats() {
    try {
      const response = await fetch(`${this.baseURL}/home-progress-stats`);
      if (!response.ok) throw new Error('Failed to fetch home progress stats');
      return await response.json();
    } catch (error) {
      console.error('Error fetching home progress stats:', error);
      throw error;
    }
  }

  async updateHomeProgressStats(data) {
    try {
      console.log('🔄 Sending PUT request to update home progress stats:', data);
      const response = await fetch(`${this.baseURL}/home-progress-stats`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT home progress stats failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update home progress stats: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT home progress stats successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating home progress stats:', error);
      throw error;
    }
  }

  // ===========================
  // HOME TESTIMONIALS API (for testimonials section)
  // ===========================

  async getHomeTestimonials() {
    try {
      const response = await fetch(`${this.baseURL}/home-testimonials`);
      if (!response.ok) throw new Error('Failed to fetch home testimonials');
      return await response.json();
    } catch (error) {
      console.error('Error fetching home testimonials:', error);
      throw error;
    }
  }

  async createHomeTestimonial(data) {
    try {
      console.log('🔄 Sending POST request to create home testimonial:', data);
      const response = await fetch(`${this.baseURL}/home-testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST home testimonial failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create home testimonial: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ POST home testimonial successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating home testimonial:', error);
      throw error;
    }
  }

  async updateHomeTestimonial(id, data) {
    try {
      console.log('🔄 Sending PUT request to update home testimonial:', { id, ...data });
      const response = await fetch(`${this.baseURL}/home-testimonials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT home testimonial failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update home testimonial: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT home testimonial successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating home testimonial:', error);
      throw error;
    }
  }

  async deleteHomeTestimonial(id) {
    try {
      const response = await fetch(`${this.baseURL}/home-testimonials?id=${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete home testimonial');
      return await response.json();
    } catch (error) {
      console.error('Error deleting home testimonial:', error);
      throw error;
    }
  }

  // ===========================
  // SERVICES API (for Services page - Dashboard/Lineup/Category)
  // Coming soon when you build the Services page
  // ===========================

  // ===========================
  // CONTACT PAGE CONFIG API (Contact page hero section)
  // ===========================

  async getContactConfig() {
    try {
      const response = await fetch(`${this.baseURL}/contact-config`);
      if (!response.ok) throw new Error('Failed to fetch contact config');
      return await response.json();
    } catch (error) {
      console.error('Error fetching contact config:', error);
      throw error;
    }
  }

  async updateContactConfig(data) {
    try {
      console.log('🔄 Sending PUT request to update contact config:', data);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/contact-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT contact config failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update contact config: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT contact config successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating contact config:', error);
      throw error;
    }
  }

  // ===========================
  // SITE CONFIG API (Global site settings - Footer, Contact page)
  // ===========================

  async getSiteConfig() {
    try {
      const response = await fetch(`${this.baseURL}/site-config`);
      if (!response.ok) throw new Error('Failed to fetch site config');
      return await response.json();
    } catch (error) {
      console.error('Error fetching site config:', error);
      throw error;
    }
  }

  async updateSiteConfig(data) {
    try {
      console.log('🔄 Sending PUT request to update site config:', data);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/site-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT site config failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update site config: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PUT site config successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating site config:', error);
      throw error;
    }
  }

  async patchSiteConfig(data) {
    try {
      console.log('🔄 Sending PATCH request to partially update site config:', data);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/site-config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH site config failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to patch site config: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ PATCH site config successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Error patching site config:', error);
      throw error;
    }
  }

  // ===========================
  // SERVICES TREE API (Lineups -> Categories -> Services)
  // ===========================

  async getServicesTree() {
    try {
      const response = await fetch(`${this.baseURL}/services-tree`);
      if (!response.ok) throw new Error('Failed to fetch services tree');
      return await response.json();
    } catch (error) {
      console.error('Error fetching services tree:', error);
      throw error;
    }
  }

  // LINEUPS
  async createLineup(data) {
    try {
      console.log('🔄 Creating lineup:', data);
      const response = await fetch(`${this.baseURL}/lineups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST lineup failed:', errorText);
        throw new Error(`Failed to create lineup: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Lineup created:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating lineup:', error);
      throw error;
    }
  }

  async updateLineup(id, data) {
    try {
      console.log('🔄 Updating lineup:', { id, ...data });
      const response = await fetch(`${this.baseURL}/lineups`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT lineup failed:', errorText);
        throw new Error(`Failed to update lineup: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Lineup updated:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating lineup:', error);
      throw error;
    }
  }

  async deleteLineup(id) {
    try {
      console.log('🗑️ Deleting lineup:', id);
      const response = await fetch(`${this.baseURL}/lineups?id=${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE lineup failed:', errorText);
        throw new Error(`Failed to delete lineup: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Lineup deleted:', result);
      return result;
    } catch (error) {
      console.error('❌ Error deleting lineup:', error);
      throw error;
    }
  }

  async reorderLineups(updates) {
    try {
      console.log('🔄 Reordering lineups:', updates);
      const response = await fetch(`${this.baseURL}/lineups`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH lineups failed:', errorText);
        throw new Error(`Failed to reorder lineups: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Lineups reordered:', result);
      return result;
    } catch (error) {
      console.error('❌ Error reordering lineups:', error);
      throw error;
    }
  }

  // CATEGORIES
  async createCategory(data) {
    try {
      console.log('🔄 Creating category:', data);
      const response = await fetch(`${this.baseURL}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST category failed:', errorText);
        throw new Error(`Failed to create category: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Category created:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating category:', error);
      throw error;
    }
  }

  async updateCategory(id, data) {
    try {
      console.log('🔄 Updating category:', { id, ...data });
      const response = await fetch(`${this.baseURL}/categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT category failed:', errorText);
        throw new Error(`Failed to update category: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Category updated:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating category:', error);
      throw error;
    }
  }

  async deleteCategory(id) {
    try {
      console.log('🗑️ Deleting category:', id);
      const response = await fetch(`${this.baseURL}/categories?id=${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE category failed:', errorText);
        throw new Error(`Failed to delete category: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Category deleted:', result);
      return result;
    } catch (error) {
      console.error('❌ Error deleting category:', error);
      throw error;
    }
  }

  async reorderCategories(updates) {
    try {
      console.log('🔄 Reordering categories:', updates);
      const response = await fetch(`${this.baseURL}/categories`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH categories failed:', errorText);
        throw new Error(`Failed to reorder categories: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Categories reordered:', result);
      return result;
    } catch (error) {
      console.error('❌ Error reordering categories:', error);
      throw error;
    }
  }

  // SERVICES
  async createService(data) {
    try {
      console.log('🔄 Creating service:', data);
      const response = await fetch(`${this.baseURL}/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST service failed:', errorText);
        throw new Error(`Failed to create service: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Service created:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating service:', error);
      throw error;
    }
  }

  async updateService(id, data) {
    try {
      console.log('🔄 Updating service:', { id, ...data });
      const response = await fetch(`${this.baseURL}/services`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT service failed:', errorText);
        throw new Error(`Failed to update service: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Service updated:', result);
      return result;
    } catch (error) {
      console.error('❌ Error updating service:', error);
      throw error;
    }
  }

  async deleteService(id) {
    try {
      console.log('🗑️ Deleting service:', id);
      const response = await fetch(`${this.baseURL}/services?id=${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE service failed:', errorText);
        throw new Error(`Failed to delete service: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Service deleted:', result);
      return result;
    } catch (error) {
      console.error('❌ Error deleting service:', error);
      throw error;
    }
  }

  async reorderServices(updates) {
    try {
      console.log('🔄 Reordering services:', updates);
      const response = await fetch(`${this.baseURL}/services`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH services failed:', errorText);
        throw new Error(`Failed to reorder services: ${response.status}`);
      }
      const result = await response.json();
      console.log('✅ Services reordered:', result);
      return result;
    } catch (error) {
      console.error('❌ Error reordering services:', error);
      throw error;
    }
  }

  // ===========================
  // SERVICE PAGE CONTENT API (Text content for service pages)
  // ===========================

  async getServicePageContent(serviceId) {
    try {
      console.log(`🔄 Fetching service page content for: ${serviceId}`);
      const response = await fetch(`${this.baseURL}/service-content/${serviceId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch service page content: ${response.status}`);
      }
      const result = await response.json();
      console.log(`✅ Loaded service page content for ${serviceId}:`, result);
      return result.content || {};
    } catch (error) {
      console.error('❌ Error fetching service page content:', error);
      throw error;
    }
  }

  async updateServicePageContent(serviceId, content) {
    try {
      console.log(`🔄 Updating service page content for: ${serviceId}`, content);
      const response = await fetch(`${this.baseURL}/service-content/${serviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT service page content failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update service page content: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Updated service page content for ${serviceId}:`, result);
      return result.content || {};
    } catch (error) {
      console.error('❌ Error updating service page content:', error);
      throw error;
    }
  }

  // ===========================
  // SERVICE WHYCHOOSE FEATURES API (Feature cards for service pages)
  // ===========================

  async getWhyChooseFeatures(serviceId) {
    try {
      console.log(`🔄 Fetching WhyChoose features for: ${serviceId}`);
      const response = await fetch(`${this.baseURL}/whychoose-features/${serviceId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch WhyChoose features: ${response.status}`);
      }
      const result = await response.json();
      console.log(`✅ Loaded ${result.features?.length || 0} WhyChoose features for ${serviceId}`);
      return result.features || [];
    } catch (error) {
      console.error('❌ Error fetching WhyChoose features:', error);
      throw error;
    }
  }

  async createWhyChooseFeature(serviceId, featureData) {
    try {
      console.log(`🔄 Creating WhyChoose feature for service: ${serviceId}`, featureData);
      const response = await fetch(`${this.baseURL}/whychoose-features/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(featureData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST WhyChoose feature failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create WhyChoose feature: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Created WhyChoose feature ID: ${result.id}`);
      return result;
    } catch (error) {
      console.error('❌ Error creating WhyChoose feature:', error);
      throw error;
    }
  }

  async updateWhyChooseFeature(featureId, featureData) {
    try {
      console.log(`🔄 Updating WhyChoose feature: ${featureId}`, featureData);
      const response = await fetch(`${this.baseURL}/whychoose-features/${featureId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(featureData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT WhyChoose feature failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update WhyChoose feature: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Updated WhyChoose feature ID: ${featureId}`);
      return result;
    } catch (error) {
      console.error('❌ Error updating WhyChoose feature:', error);
      throw error;
    }
  }

  async deleteWhyChooseFeature(featureId) {
    try {
      console.log(`🗑️ Deleting WhyChoose feature: ${featureId}`);
      const response = await fetch(`${this.baseURL}/whychoose-features/${featureId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE WhyChoose feature failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to delete WhyChoose feature: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Deleted WhyChoose feature ID: ${featureId}`);
      return result;
    } catch (error) {
      console.error('❌ Error deleting WhyChoose feature:', error);
      throw error;
    }
  }

  async reorderWhyChooseFeatures(serviceId, updates) {
    try {
      console.log(`🔄 Reordering WhyChoose features for service: ${serviceId}`, updates);
      const response = await fetch(`${this.baseURL}/whychoose-features/${serviceId}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH WhyChoose features failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to reorder WhyChoose features: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Reordered ${updates.length} WhyChoose features for ${serviceId}`);
      return result;
    } catch (error) {
      console.error('❌ Error reordering WhyChoose features:', error);
      throw error;
    }
  }

  // ===========================
  // SERVICE SLIDES API (Carousel slides for service pages)
  // ===========================

  async getSlides(serviceId) {
    try {
      console.log(`🔄 Fetching slides for: ${serviceId}`);
      const response = await fetch(`${this.baseURL}/slides/${serviceId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch slides: ${response.status}`);
      }
      const result = await response.json();
      console.log(`✅ Loaded ${result.slides?.length || 0} slides for ${serviceId}`);
      return result.slides || [];
    } catch (error) {
      console.error('❌ Error fetching slides:', error);
      throw error;
    }
  }

  async createSlide(serviceId, slideData) {
    try {
      console.log(`🔄 Creating slide for service: ${serviceId}`, slideData);
      const response = await fetch(`${this.baseURL}/slides/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slideData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST slide failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to create slide: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Created slide ID: ${result.id}`);
      return result;
    } catch (error) {
      console.error('❌ Error creating slide:', error);
      throw error;
    }
  }

  async updateSlide(slideId, slideData) {
    try {
      console.log(`🔄 Updating slide: ${slideId}`, slideData);
      const response = await fetch(`${this.baseURL}/slides/${slideId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(slideData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT slide failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to update slide: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Updated slide ID: ${slideId}`);
      return result;
    } catch (error) {
      console.error('❌ Error updating slide:', error);
      throw error;
    }
  }

  async deleteSlide(slideId) {
    try {
      console.log(`🗑️ Deleting slide: ${slideId}`);
      const response = await fetch(`${this.baseURL}/slides/${slideId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE slide failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to delete slide: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Deleted slide ID: ${slideId}`);
      return result;
    } catch (error) {
      console.error('❌ Error deleting slide:', error);
      throw error;
    }
  }

  async reorderSlides(serviceId, updates) {
    try {
      console.log(`🔄 Reordering slides for service: ${serviceId}`, updates);
      const response = await fetch(`${this.baseURL}/slides/${serviceId}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH slides failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        throw new Error(`Failed to reorder slides: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Reordered ${updates.length} slides for ${serviceId}`);
      return result;
    } catch (error) {
      console.error('❌ Error reordering slides:', error);
      throw error;
    }
  }

  // ===========================
  // SLIDEGALLERY API (Gallery items for service pages)
  // ===========================

  async getSlideGalleryItems(serviceId) {
    try {
      console.log(`📥 Fetching slide gallery items for service: ${serviceId}`);
      const response = await fetch(`${this.baseURL}/slidegallery/${serviceId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch slide gallery items: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Fetched ${result.items?.length || 0} gallery items for ${serviceId}`);
      return result.items || [];
    } catch (error) {
      console.error('❌ Error fetching slide gallery items:', error);
      throw error;
    }
  }

  async createSlideGalleryItem(serviceId, itemData) {
    try {
      console.log(`➕ Creating slide gallery item for service: ${serviceId}`, itemData);
      const response = await fetch(`${this.baseURL}/slidegallery/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ POST slide gallery item failed:', errorText);
        throw new Error(`Failed to create slide gallery item: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Created slide gallery item with ID: ${result.id}`);
      return result;
    } catch (error) {
      console.error('❌ Error creating slide gallery item:', error);
      throw error;
    }
  }

  async updateSlideGalleryItem(itemId, itemData) {
    try {
      console.log(`✏️ Updating slide gallery item: ${itemId}`, itemData);
      const response = await fetch(`${this.baseURL}/slidegallery/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PUT slide gallery item failed:', errorText);
        throw new Error(`Failed to update slide gallery item: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Updated slide gallery item: ${itemId}`);
      return result;
    } catch (error) {
      console.error('❌ Error updating slide gallery item:', error);
      throw error;
    }
  }

  async deleteSlideGalleryItem(itemId) {
    try {
      console.log(`🗑️ Deleting slide gallery item: ${itemId}`);
      const response = await fetch(`${this.baseURL}/slidegallery/${itemId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ DELETE slide gallery item failed:', errorText);
        throw new Error(`Failed to delete slide gallery item: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Deleted slide gallery item: ${itemId}`);
      return result;
    } catch (error) {
      console.error('❌ Error deleting slide gallery item:', error);
      throw error;
    }
  }

  async reorderSlideGalleryItems(serviceId, updates) {
    try {
      console.log(`🔄 Reordering slide gallery items for service: ${serviceId}`, updates);
      const response = await fetch(`${this.baseURL}/slidegallery/${serviceId}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ PATCH slide gallery items failed:', errorText);
        throw new Error(`Failed to reorder slide gallery items: ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ Reordered ${updates.length} gallery items for ${serviceId}`);
      return result;
    } catch (error) {
      console.error('❌ Error reordering slide gallery items:', error);
      throw error;
    }
  }

  // ===========================
  // STEPCONTAINER STEPS API
  // ===========================

  async getStepContainerSteps(serviceId) {
    try {
      console.log(`📥 Fetching stepcontainer steps for service: ${serviceId}`);
      const response = await fetch(`${this.baseURL}/stepcontainer/${serviceId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch stepcontainer steps: ${response.status}`);
      }

      const steps = await response.json();
      console.log(`✅ Fetched ${steps.length} stepcontainer steps for ${serviceId}`);
      return steps;
    } catch (error) {
      console.error('❌ Error fetching stepcontainer steps:', error);
      throw error;
    }
  }

  async createStepContainerStep(serviceId, stepData) {
    try {
      console.log(`➕ Creating stepcontainer step for service: ${serviceId}`, stepData);
      const response = await fetch(`${this.baseURL}/stepcontainer/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stepData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create stepcontainer step: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Created stepcontainer step:', result);
      return result;
    } catch (error) {
      console.error('❌ Error creating stepcontainer step:', error);
      throw error;
    }
  }

  async updateStepContainerStep(stepId, stepData) {
    try {
      console.log(`✏️ Updating stepcontainer step ${stepId}`, stepData);
      const response = await fetch(`${this.baseURL}/stepcontainer/step/${stepId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stepData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update stepcontainer step: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Updated stepcontainer step ${stepId}`);
      return result;
    } catch (error) {
      console.error('❌ Error updating stepcontainer step:', error);
      throw error;
    }
  }

  async deleteStepContainerStep(stepId) {
    try {
      console.log(`🗑️ Deleting stepcontainer step ${stepId}`);
      const response = await fetch(`${this.baseURL}/stepcontainer/step/${stepId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete stepcontainer step: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Deleted stepcontainer step ${stepId}`);
      return result;
    } catch (error) {
      console.error('❌ Error deleting stepcontainer step:', error);
      throw error;
    }
  }

  async reorderStepContainerSteps(serviceId, updates) {
    try {
      console.log(`🔄 Reordering stepcontainer steps for service: ${serviceId}`, updates);
      const response = await fetch(`${this.baseURL}/stepcontainer/${serviceId}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to reorder stepcontainer steps: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log(`✅ Reordered ${updates.length} stepcontainer steps for ${serviceId}`);
      return result;
    } catch (error) {
      console.error('❌ Error reordering stepcontainer steps:', error);
      throw error;
    }
  }

  // ===========================
  // BLOG POSTS API (Last priority - Coming soon)
  // ===========================

  // Placeholder for blog API
  // Will implement last according to your priority

  // ===========================
  // COMPARISON FEATURES API (Service Page Comparison Section)
  // ===========================

  async getComparisonFeatures(serviceId) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-features/${serviceId}`);
      if (!response.ok) throw new Error('Failed to fetch comparison features');
      return await response.json();
    } catch (error) {
      console.error('Error fetching comparison features:', error);
      throw error;
    }
  }

  async createComparisonFeature(serviceId, featureData) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-features/${serviceId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(featureData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create comparison feature');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating comparison feature:', error);
      throw error;
    }
  }

  async updateComparisonFeature(featureId, featureData) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-features/${featureId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(featureData)
      });
      if (!response.ok) throw new Error('Failed to update comparison feature');
      return await response.json();
    } catch (error) {
      console.error('Error updating comparison feature:', error);
      throw error;
    }
  }

  async deleteComparisonFeature(featureId) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-features/${featureId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete comparison feature');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting comparison feature:', error);
      throw error;
    }
  }

  async reorderComparisonFeatures(serviceId, updates) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-features/${serviceId}/reorder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Failed to reorder comparison features');
      return await response.json();
    } catch (error) {
      console.error('Error reordering comparison features:', error);
      throw error;
    }
  }

  // ===========================
  // COMPARISON MATRIX API (Category-Wide Comparison Table)
  // ===========================

  async getCategoryComparisonMatrix(categoryId) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-matrix/${categoryId}`);
      if (!response.ok) throw new Error('Failed to fetch category comparison matrix');
      return await response.json();
    } catch (error) {
      console.error('Error fetching category comparison matrix:', error);
      throw error;
    }
  }

  async saveCategoryComparisonMatrix(categoryId, features) {
    try {
      const response = await fetch(`${this.baseURL}/comparison-matrix/${categoryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ features })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save comparison matrix');
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving category comparison matrix:', error);
      throw error;
    }
  }

  async getServiceCategory(serviceId) {
    try {
      const response = await fetch(`${this.baseURL}/service-category/${serviceId}`);
      if (!response.ok) throw new Error('Failed to fetch service category');
      return await response.json();
    } catch (error) {
      console.error('Error fetching service category:', error);
      throw error;
    }
  }

  // ===========================
  // SITE CONFIG API (Global Site Settings)
  // ===========================

  async getSiteConfig() {
    try {
      const response = await fetch(`${this.baseURL}/site-config`);
      if (!response.ok) throw new Error('Failed to fetch site config');
      return await response.json();
    } catch (error) {
      console.error('Error fetching site config:', error);
      throw error;
    }
  }

  // ===========================
  // AUTHENTICATION API
  // ===========================

  async login(email, password, rememberMe = false) {
    try {
      const response = await fetch(`${this.baseURL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  }

  async logout(token) {
    try {
      const response = await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!response.ok) throw new Error('Logout failed');
      return await response.json();
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  }

  async verifySession(token) {
    try {
      const response = await fetch(`${this.baseURL}/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return { valid: false };
      return await response.json();
    } catch (error) {
      console.error('Error verifying session:', error);
      return { valid: false };
    }
  }

  async changePassword(token, currentPassword, newPassword) {
    try {
      const response = await fetch(`${this.baseURL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Password change failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  // ===========================
  // USER MANAGEMENT API
  // ===========================

  async getUsers() {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async createUser(userData) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create user');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUserRole(userId, role) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users?id=${userId}&action=change-role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update role');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  async updateUser(userId, updates) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users?id=${userId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users?id=${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete user');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // ===========================
  // MEDIA ITEMS API (URL-based media library)
  // ===========================

  async getMediaItems(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.category) queryParams.append('category', params.category);
      if (params.search) queryParams.append('search', params.search);
      if (params.id) queryParams.append('id', params.id);

      const url = `${this.baseURL}/media${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Failed to fetch media items');
      return await response.json();
    } catch (error) {
      console.error('Error fetching media items:', error);
      throw error;
    }
  }

  async createMediaItem(data) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create media item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating media item:', error);
      throw error;
    }
  }

  async updateMediaItem(mediaId, data) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/media?id=${mediaId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update media item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating media item:', error);
      throw error;
    }
  }

  async deleteMediaItem(mediaId) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/media?id=${mediaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete media item');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting media item:', error);
      throw error;
    }
  }

  async getUserDetails(userId) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/users?id=${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch user details');
      return await response.json();
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  // ===========================
  // HOME FEATURE GALLERY API
  // ===========================

  async getFeatureGalleryItems() {
    try {
      const response = await fetch(`${this.baseURL}/home-feature-gallery`);
      if (!response.ok) throw new Error('Failed to fetch gallery items');
      return await response.json();
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      throw error;
    }
  }

  async createFeatureGalleryItem(data) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/home-feature-gallery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create gallery item');
      return await response.json();
    } catch (error) {
      console.error('Error creating gallery item:', error);
      throw error;
    }
  }

  async updateFeatureGalleryItem(data) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/home-feature-gallery`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update gallery item');
      return await response.json();
    } catch (error) {
      console.error('Error updating gallery item:', error);
      throw error;
    }
  }

  async deleteFeatureGalleryItem(id) {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${this.baseURL}/home-feature-gallery?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      if (!response.ok) throw new Error('Failed to delete gallery item');
      return await response.json();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      throw error;
    }
  }

  // ===========================
  // UTILITY METHODS
  // ===========================

  // Check if API is reachable
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`);
      return response.ok;
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }

  // ==========================================
  // R2 FILE UPLOAD
  // ==========================================

  /**
   * Upload file to R2 bucket
   * @param {File} file - File object from input
   * @param {string} category - Category for organizing files
   * @param {string} altText - Alt text for images
   * @param {string} tags - Comma-separated tags
   * @returns {Promise<Object>} Uploaded file metadata
   */
  async uploadFile(file, category = 'general', altText = '', tags = '') {
    try {
      console.log('📤 [API] Uploading file:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
      console.log('📤 [API] Target URL:', `${this.baseURL}/upload`);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      formData.append('alt_text', altText);
      formData.append('tags', tags);

      console.log('📤 [API] Sending request...');
      const response = await fetch(`${this.baseURL}/upload`, {
        method: 'POST',
        body: formData
        // Note: Don't set Content-Type header, browser will set it with boundary
      });

      console.log('📥 [API] Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        console.error('❌ [API] Response not OK, parsing error...');
        const error = await response.json();
        console.error('❌ [API] Error response:', error);
        throw new Error(error.error || 'Upload failed');
      }

      console.log('📥 [API] Parsing success response...');
      const result = await response.json();
      console.log('✅ [API] Upload successful, result:', result);
      console.log('✅ [API] Returning item:', result.item);
      return result.item;
    } catch (error) {
      console.error('❌ [API] Upload error caught:', error);
      console.error('❌ [API] Error type:', error.constructor.name);
      console.error('❌ [API] Error message:', error.message);
      throw error;
    }
  }

  // ===========================
  // CONTACT FORM API
  // ===========================

  async submitContactForm(formData) {
    try {
      console.log('📨 Submitting contact form...');

      const response = await fetch(`${this.baseURL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      console.log('✅ Contact form submitted successfully');
      return result;
    } catch (error) {
      console.error('❌ Contact form submission error:', error);
      throw error;
    }
  }

  // ===========================
  // BLOG API
  // ===========================

  // Get all blog posts (with optional filtering)
  async getBlogPosts(category = null, search = null) {
    try {
      let url = `${this.baseURL}/blog/posts`;
      const params = new URLSearchParams();

      if (category && category !== 'Latest') {
        params.append('category', category);
      }
      if (search) {
        params.append('search', search);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch blog posts');
      const result = await response.json();
      return result.posts || [];
    } catch (error) {
      console.error('❌ Error fetching blog posts:', error);
      throw error;
    }
  }

  // Get single blog post by slug
  async getBlogPost(slug) {
    try {
      const response = await fetch(`${this.baseURL}/blog/${slug}`);
      if (!response.ok) throw new Error('Failed to fetch blog post');
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching blog post:', error);
      throw error;
    }
  }

  // Create new blog post
  async createBlogPost(postData) {
    try {
      const response = await fetch(`${this.baseURL}/blog/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create blog post');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Error creating blog post:', error);
      throw error;
    }
  }

  // Update blog post
  async updateBlogPost(slug, postData) {
    try {
      const response = await fetch(`${this.baseURL}/blog/${slug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(postData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update blog post');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Error updating blog post:', error);
      throw error;
    }
  }

  // Delete blog post
  async deleteBlogPost(slug) {
    try {
      const response = await fetch(`${this.baseURL}/blog/${slug}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete blog post');
      return await response.json();
    } catch (error) {
      console.error('❌ Error deleting blog post:', error);
      throw error;
    }
  }

  // Get blog categories
  async getBlogCategories() {
    try {
      const response = await fetch(`${this.baseURL}/blog/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const result = await response.json();
      return result.categories || ['Latest'];
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      // Return default categories on error
      return ['Latest', 'Photo Editing', 'Real Estate', 'Wedding', 'Product Photography', 'Tips & Tricks'];
    }
  }

  // Create new category
  async createBlogCategory(name) {
    try {
      const response = await fetch(`${this.baseURL}/blog/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to create category');
      }
      return result;
    } catch (error) {
      console.error('❌ Error creating category:', error);
      throw error;
    }
  }

  // Delete category
  async deleteBlogCategory(name) {
    try {
      const response = await fetch(`${this.baseURL}/blog/categories?name=${encodeURIComponent(name)}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete category');
      }
      return result;
    } catch (error) {
      console.error('❌ Error deleting category:', error);
      throw error;
    }
  }

  // ===========================
  // ASSET CATEGORIES API
  // ===========================

  // Get all asset categories
  async getAssetCategories() {
    try {
      const response = await fetch(`${this.baseURL}/asset-categories`);
      if (!response.ok) throw new Error('Failed to fetch asset categories');
      return await response.json();
    } catch (error) {
      console.error('❌ Error fetching asset categories:', error);
      throw error;
    }
  }

  // Create new asset category
  async createAssetCategory(data) {
    try {
      const response = await fetch(`${this.baseURL}/asset-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create asset category');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Error creating asset category:', error);
      throw error;
    }
  }

  // Update asset category
  async updateAssetCategory(id, data) {
    try {
      const response = await fetch(`${this.baseURL}/asset-categories`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...data })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update asset category');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Error updating asset category:', error);
      throw error;
    }
  }

  // Delete asset category
  async deleteAssetCategory(id) {
    try {
      const response = await fetch(`${this.baseURL}/asset-categories/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete asset category');
      }
      return await response.json();
    } catch (error) {
      console.error('❌ Error deleting asset category:', error);
      throw error;
    }
  }

  // ===========================
  // CUSTOMERS API
  // ===========================

  async getCustomers() {
    try {
      const response = await fetch(`${this.baseURL}/customers`);
      if (!response.ok) throw new Error('Failed to fetch customers');
      return await response.json();
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  async createCustomer(customerData) {
    try {
      const response = await fetch(`${this.baseURL}/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create customer');
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  async updateCustomer(customerId, customerData) {
    try {
      const response = await fetch(`${this.baseURL}/customers?id=${customerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update customer');
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  async deleteCustomer(customerId) {
    try {
      const response = await fetch(`${this.baseURL}/customers?id=${customerId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete customer');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new CloudflareAPI();
